import { getMappedDataSource } from '../../utils/get-mapped-data-source/get-mapped-data-source';
import { isMergedDataSourceValue } from '../../utils/get-mapped-data-source/get-merged-data-source-value';
import type { UseDataSourceProps } from '../../types/hooks';

/**
 * Hook that processes entity data sources (local entity and relationships data) and applies mapping transformations if needed.
 *
 * This hook handles data source values, applying the appropriate mapping based on the consumer type
 * and configuration. It supports both array data sources and merged data sources.
 *
 * @param {UseDataSourceProps} props - The data source props
 * @param {object} props.dataSource - The data source object containing the values to be processed
 * @param {object} [props.dataSourceConsumer] - The consumer configuration that defines how the data should be mapped
 * @param {string} [props.dataSourceConsumer.type] - The type of data consumer (e.g., 'enumOptions', 'groupedEnumOptions')
 * @param {object} [props.dataSourceConsumer.mapping] - The mapping configuration for transforming the data
 * @returns {any} The mapped data source value if mapping is applied, or the raw data source value
 */

export const useEntityFormDataSource = ({
  dataSource,
  dataSourceConsumer,
}: UseDataSourceProps) => {
  if (
    dataSourceConsumer?.mapping &&
    (Array.isArray(dataSource.value) ||
      isMergedDataSourceValue(dataSource.value))
  ) {
    return getMappedDataSource({
      dataSourceValue: dataSource.value,
      type: dataSourceConsumer.type,
      mapping: dataSourceConsumer.mapping,
    });
  }

  return dataSource.value;
};
